import React, { useState, useContext, useEffect } from 'react';
import { AuthContext } from '../../components/AuthContext';
import { Link } from 'react-router-dom';
import axios from 'axios';
import Swal from 'sweetalert2';
import { FaExclamationTriangle, FaFileExcel, FaEye } from 'react-icons/fa';
import { Pagination } from '@mui/material';

const QuotationManagement = () => {
  const { user } = useContext(AuthContext);

  // Quotation list pagination
  const [quotationPage, setQuotationPage] = useState(1);
  const [quotationLimit, setQuotationLimit] = useState(10);
  const [totalQuotations, setTotalQuotations] = useState(0);
  const [quotations, setQuotations] = useState([]);
  const [isLoadingExport, setIsLoadingExport] = useState(false);
  
  const quotationTotalPages = Math.ceil(totalQuotations / quotationLimit);

  // ======================
  //    FETCH ALL QUOTATIONS (NO PAGINATION FOR VIEWING)
  // ======================
  const fetchAllQuotations = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/quotations/all/${user.user._id}`
      );
      setQuotations(response.data.quotations);
      setTotalQuotations(response.data.total);
    } catch (error) {
      console.error('Error fetching quotations:', error);
      Swal.fire({
        title: 'Error!',
        text: 'Failed to fetch quotations',
        icon: 'error',
        confirmButtonText: 'OK'
      });
    }
  };

  useEffect(() => {
    if (user?.user?._id) {
      fetchAllQuotations();
    }
  }, [user?.user?._id]);

  // ======================
  //    EXPORT SINGLE QUOTATION
  // ======================
  const handleExportSingleQuotation = async (quotationId, quotationNumber) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/quotations/export/single/${quotationId}`,
        {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );

      if (!response.ok) {
        throw new Error('Export failed');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `quotation_${quotationNumber}_detailed.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      Swal.fire({
        title: 'Success!',
        text: `Quotation ${quotationNumber} exported successfully`,
        icon: 'success',
        confirmButtonText: 'OK'
      });
    } catch (error) {
      console.error('Error exporting quotation:', error);
      Swal.fire({
        title: 'Error!',
        text: 'Failed to export quotation',
        icon: 'error',
        confirmButtonText: 'OK'
      });
    }
  };

  // ======================
  //    EXPORT ALL TO EXCEL
  // ======================
  const handleExportExcel = async () => {
    if (quotations.length === 0) {
      Swal.fire({
        title: 'No Data',
        text: 'No quotations found to export',
        icon: 'warning',
        confirmButtonText: 'OK'
      });
      return;
    }

    setIsLoadingExport(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/quotations/export/excel/${user.user._id}`,
        {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );

      if (!response.ok) {
        throw new Error('Export failed');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `quotations_${new Date().toISOString().split('T')[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      Swal.fire({
        title: 'Success!',
        text: 'Quotations exported successfully',
        icon: 'success',
        confirmButtonText: 'OK'
      });
    } catch (error) {
      console.error('Error exporting quotations:', error);
      Swal.fire({
        title: 'Error!',
        text: 'Failed to export quotations',
        icon: 'error',
        confirmButtonText: 'OK'
      });
    } finally {
      setIsLoadingExport(false);
    }
  };

  // Helper to format numbers with commas
  const addCommas = (value) => {
    if (value === null || value === undefined) return '0';
    return value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  };

  // ================================================
  //                    RENDER
  // ================================================
  return (
    <div className="min-h-screen bg-gray-50 p-4 sm:p-6 lg:p-8">
      <div className="w-full mx-auto">
        
        {/* Deprecation Notice */}
        <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-6">
          <div className="flex">
            <div className="flex-shrink-0">
              <FaExclamationTriangle className="h-5 w-5 text-yellow-400" />
            </div>
            <div className="ml-3">
              <p className="text-sm text-yellow-700">
                <strong>This quotation module is deprecated.</strong> Please use the{' '}
                <Link 
                  to="/quotation-new" 
                  className="font-medium underline text-yellow-700 hover:text-yellow-600"
                >
                  new quotation module
                </Link>{' '}
                for creating new quotations.
              </p>
            </div>
          </div>
        </div>

        {/* Header */}
        <div className="bg-white shadow rounded-lg mb-6">
          <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
            <div className="flex justify-between items-center">
              <div>
                <h3 className="text-lg leading-6 font-medium text-gray-900">
                  Your Quotations (Read Only)
                </h3>
                <p className="mt-1 max-w-2xl text-sm text-gray-500">
                  View and export your existing quotations. Total: {totalQuotations}
                </p>
              </div>
              <button
                onClick={handleExportExcel}
                disabled={isLoadingExport || quotations.length === 0}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:bg-gray-400"
              >
                <FaFileExcel className="mr-2" />
                {isLoadingExport ? 'Exporting...' : 'Export Excel'}
              </button>
            </div>
          </div>
        </div>

        {/* Quotations List */}
        <div className="bg-white shadow overflow-hidden sm:rounded-md">
          <ul className="divide-y divide-gray-200">
            {quotations.length === 0 ? (
              <li className="px-4 py-12 text-center">
                <div className="text-gray-400">
                  <p className="text-lg font-medium">No quotations found</p>
                  <p className="text-sm">You haven't created any quotations yet.</p>
                </div>
              </li>
            ) : (
              quotations.map((quotation, index) => (
                <li key={quotation._id} className="hover:bg-gray-50">
                  <div className="px-4 py-4 sm:px-6">
                    <div className="flex items-center justify-between">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm font-medium text-indigo-600 truncate">
                              {quotation.id}
                            </p>
                            <p className="text-sm text-gray-500">
                              {quotation.recipientType}: {
                                quotation.recipient?.name || 
                                quotation.recipient?.businessName || 
                                quotation.recipient?.customerName || 
                                'Unknown Recipient'
                              }
                            </p>
                          </div>
                          <div className="text-right">
                            <p className="text-sm font-medium text-gray-900">
                              ₹{addCommas(quotation.totalAmount)}
                            </p>
                            <p className="text-xs text-gray-500">
                              {new Date(quotation.createdAt).toLocaleDateString()}
                            </p>
                          </div>
                        </div>
                        
                        <div className="mt-2">
                          <div className="flex items-center text-sm text-gray-500">
                            <span className="truncate">
                              Salesperson: {quotation.salesman?.map(s => s.employee?.name || 'Unknown').join(', ')}
                            </span>
                          </div>
                          
                          {quotation.sections && quotation.sections.length > 0 && (
                            <div className="mt-1 text-xs text-gray-400">
                              {quotation.sections.length} section(s), {
                                quotation.sections.reduce((total, section) => 
                                  total + (section.items?.length || 0), 0
                                )
                              } item(s)
                            </div>
                          )}
                          
                          {quotation.remarks && (
                            <div className="mt-1 text-xs text-gray-600 italic">
                              "{quotation.remarks}"
                            </div>
                          )}
                        </div>
                        
                        <div className="mt-2 flex items-center space-x-4 text-xs text-gray-500">
                          {quotation.specialDiscount > 0 && (
                            <span>Special Discount: ₹{addCommas(quotation.specialDiscount)}</span>
                          )}
                          {quotation.cartage > 0 && (
                            <span>Cartage: ₹{addCommas(quotation.cartage)}</span>
                          )}
                          {quotation.labour > 0 && (
                            <span>Labour: ₹{addCommas(quotation.labour)}</span>
                          )}
                          {quotation.packingAndForwarding > 0 && (
                            <span>P&F: ₹{addCommas(quotation.packingAndForwarding)}</span>
                          )}
                        </div>
                      </div>
                      
                      <div className="ml-4 flex-shrink-0 flex items-center space-x-2">
                        <button
                          onClick={() => handleExportSingleQuotation(quotation._id, quotation.id)}
                          className="inline-flex items-center px-2 py-1 border border-green-300 text-xs font-medium rounded text-green-700 bg-green-50 hover:bg-green-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                          title="Export detailed quotation to Excel"
                        >
                          <FaFileExcel className="mr-1" />
                          Export
                        </button>
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          quotation.status === 'APPROVED' ? 'bg-green-100 text-green-800' :
                          quotation.status === 'REJECTED' ? 'bg-red-100 text-red-800' :
                          'bg-yellow-100 text-yellow-800'
                        }`}>
                          {quotation.status || 'PENDING'}
                        </span>
                      </div>
                    </div>
                  </div>
                </li>
              ))
            )}
          </ul>
        </div>
        
        {/* Call to Action */}
        <div className="mt-8 text-center">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
            <h3 className="text-lg font-medium text-blue-900 mb-2">
              Ready to create a new quotation?
            </h3>
            <p className="text-blue-700 mb-4">
              Use our improved quotation module with enhanced features and better user experience.
            </p>
            <Link
              to="/quotation-new"
              className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Go to New Quotation Module
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuotationManagement;